/* File copied from app3 validation.js */

import * as yup from 'yup';
import i18n from 'i18next';
import { Map } from 'immutable';

/**
 * @file
 * Utility functions building on top of yup.js, our underlying validation library.
 */

type ValidatorResult = {
  err?: {
    errors: string[];
  };
  result?: any;
};
type Validator = (...args: any[]) => Promise<ValidatorResult>;
type FieldValidationNoContext = [string, Validator, any];
type FieldValidationContext = [
  string,
  Validator,
  any,
  {
    [key: string]: string | null | undefined;
  }
];
export type FieldValidation = FieldValidationContext | FieldValidationNoContext;
type FieldValidationResult = ValidatorResult & {
  fieldName: string;
  value: any;
};
type FieldValidationPromise = Promise<FieldValidationResult>;
type ValidationErrors<FieldNameType extends string> = Record<FieldNameType, string>;
type ValidationModifiedValues = {
  [key: string]: any;
};

/**
 * Returns basically what we had been using for `validateIfError` in components.
 * This takes in a validator function and returns a func for components to use to revalidate
 * if there's an error present that lines up with one (or one of) the fieldNames.
 * @param {Function} validateFn - the validatation function that is invoked if necessary in the curried fn
 */
export const createComponentRevalidator =
  (validateFn: (any) => any) => (fieldName: string | Array<string>, stateErrors: any) => {
    if (Array.isArray(fieldName)) {
      // reduce the array to those contained in errors
      const toValidate = fieldName.filter((f) => stateErrors[f]);
      if (toValidate.length) {
        validateFn(toValidate);
      }
    } else if (stateErrors[fieldName]) {
      validateFn(fieldName);
    }
  };

export function validateField(
  fieldName: string,
  validator: Validator,
  value: any,
  ...additionalArgs: any[]
): FieldValidationPromise {
  return validator(value, ...additionalArgs).then((result) => ({
    ...result,
    fieldName,
    value,
  }));
}

export function errorsFromFieldValidation<FieldNameType extends string>(
  validationResult: FieldValidationResult,
  allErrors: ValidationErrors<FieldNameType>,
  translationContext?: {
    [key: string]: any;
  }
): ValidationErrors<FieldNameType> {
  const { fieldName, err } = validationResult;
  if (err) {
    return {
      ...allErrors,
      [fieldName]: i18n.t(err.errors[0], translationContext),
    };
  } else {
    const errors = { ...allErrors } as const;
    delete errors[fieldName];
    return errors;
  }
}

export function modifiedValuesFromFieldValidation<FieldNameType extends string>(
  validationResult: FieldValidationResult,
  allModifiedValues: Partial<Record<FieldNameType, any>> = {}
): Partial<Record<FieldNameType, any>> {
  const { fieldName, err, result, value } = validationResult;
  if (!err && result !== value) {
    return { ...allModifiedValues, [fieldName]: result };
  } else {
    return allModifiedValues;
  }
}

function updateErrors<FieldNameType extends string>(
  allErrors: ValidationErrors<FieldNameType>,
  fieldName: FieldNameType,
  errorMessage?: string
): ValidationErrors<FieldNameType> {
  if (errorMessage) {
    return {
      ...allErrors,
      [fieldName]: errorMessage,
    };
  } else {
    const errors = { ...allErrors } as const;
    delete errors[fieldName];
    return errors;
  }
}

/**
 * Helper function that takes in a list of FieldValidations and then constructs and runs a list of yup validators.
 * The function also handles validating and updating a single field via the `existingErrors` and `fieldNameFilter` param.
 *
 * Example usage:
 *
 *   type FieldNames = 'size' | 'customColor';
 *
 *   const getValidations = (): Array<FieldValidation<FieldNames>> => {
 *     const { size, containerSize, customColor } = this.state
 *     const validations = []
 *
 *     // `validators.smallerThanContainer` takes multiple parameters, pass in multiple via array
 *     validations.push(['size', validators.smallerThanContainer, [size, containerSize]]);
 *     if (state.customColorSelected) {
 *       // `validators.characterLimit50` just takes one parameter, pass in value
 *       validations.push(['customColor', validators.characterLimit50, customColor]);
 *     }
 *
 *     return validations;
 *   };
 *
 *   validate = async (fieldName?: FieldNames) => {
 *     const { modifiedValues, errors } = await runValidations(this.getValidations(), this.state.errors, fieldName);
 *     this.setState({ errors });
 *     // Handle modifiedValues if needed
 *     // If the `FieldNames` for a field is identical to its corresponding `state` property, you can do: this.setState({ errors, ...modifiedValues });
 *     return Object.keys(errors).length === 0;
 *   };
 *
 */
export async function runValidations<FieldNameType extends string>(
  validations: Array<FieldValidation>,
  existingErrors: ValidationErrors<string>,
  fieldNameFilter?: Array<string> | null | FieldNameType
): Promise<{
  modifiedValues: ValidationModifiedValues;
  errors: ValidationErrors<FieldNameType>;
}> {
  const validationPromises = validations
    .filter((validation) => {
      // Don't validate this field if `fieldNameFilter` is set and this validation doesn't match
      const [fieldName] = validation;
      // fieldNames can be either a single value, or an array of values
      if (Array.isArray(fieldNameFilter)) {
        return fieldNameFilter.includes(fieldName);
      }
      return !fieldNameFilter || fieldNameFilter === fieldName;
    })
    .map((validation) => {
      const [fieldName, validator, validatorParams] = validation;
      // validatorParams can be a single value, or an array of values
      if (Array.isArray(validatorParams)) {
        const [value, ...additionalArgs] = validatorParams;
        return validateField(fieldName, validator, value, ...additionalArgs);
      }
      return validateField(fieldName, validator, validatorParams);
    });

  const translationContexts = validations.reduce((contexts, validation) => {
    if (validation.length === 4) {
      const [fieldName, , , context] = validation;
      if (context) {
        return contexts.set(fieldName, context);
      }
      return contexts;
    }
    return contexts;
  }, Map());

  let errors: ValidationErrors<string> = {};
  let modifiedValues: Record<string, any> = {};

  (await Promise.all(validationPromises)).forEach((validationResult) => {
    errors = errorsFromFieldValidation(
      validationResult,
      errors,
      // @ts-expect-error Immutable.Map.get signature differs from native Map
      translationContexts.get(validationResult.fieldName, undefined)
    );
    modifiedValues = modifiedValuesFromFieldValidation(validationResult, modifiedValues);
  });

  if (fieldNameFilter) {
    // Only update filtered field
    if (Array.isArray(fieldNameFilter)) {
      fieldNameFilter.forEach((fieldName) => {
        existingErrors = updateErrors(existingErrors, fieldName, errors[fieldName]);
      });
    } else {
      existingErrors = updateErrors(existingErrors, fieldNameFilter, errors[fieldNameFilter]);
    }
    return { modifiedValues, errors: existingErrors };
  }
  return { modifiedValues, errors };
}

/**
 * Returns an async validator function that runs a value against a static yup schema.
 *
 * Validator returns an object shaped like { result, err }, rather than yup's
 * default behavior of throwing exceptions.
 *
 * This avoids propagating errors thrown by yup when validation fails, since that is an expected,
 * not an exceptional event.
 *
 * @returns {Object} result - Object with shape { result, err }
 */
export function buildValidator(schema: any) {
  return async function (value: any): Promise<ValidatorResult> {
    try {
      const result = await schema.validate(value, { abortEarly: false });
      return { result }; // no errors
    } catch (err: any) {
      return { err };
    }
  };
}

/**
 * Creates an async validator function that runs a value against a complex yup schema.
 *  This can include values selected from the Redux store, partially
 *  applied at validation time.
 *
 * @param {yup.Schema} baseSchema - a static yup schema on to which we can
 *   concatenate dynamically created schema.
 * @param {Array<yup.Schema>} yupSchemaCreators - functions that take in
 *   the values object of a `customValidation` definition and return a yup schema.
 */
export function buildComplexValidator(baseSchema: yup.YupSchema = yup.mixed(), yupSchemaCreators: yup.YupSchema = []) {
  return async function (customValidationValues: any): Promise<ValidatorResult> {
    // Partially apply `customValidation` values
    // into individual yup schemas, and combine them all.
    const combinedSchema = yupSchemaCreators.reduce(
      (prevSchema, schemaCreator) => prevSchema.concat(schemaCreator(customValidationValues)),
      baseSchema
    );

    const { value } = customValidationValues;

    try {
      const result = await combinedSchema.validate(value, {
        abortEarly: false,
      });
      return { result }; // no errors
    } catch (err: any) {
      return { err };
    }
  };
}
